import { useFormik } from 'formik';

// import { useCallAPI } from 'hooks/useCallAPI';
import { useCampaignStore } from 'store/campaignStore';
import { useOrderStore } from 'store/orderStore';
import { BidMode, RoasStrategy } from 'core/rtbCampaign/singlePage/keyword';
import {
  ActionType,
  BudgetType,
  SelectPattern,
} from 'core/rtbCampaign/singlePage/common';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { useProductStore } from 'store/productStore';
import { SelectOptions } from 'components/commonType';
import { useCallAPI } from 'hooks/useCallAPI';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import { Product, ProductWithPagination } from 'core/product/Product';
import useRetailerManagement from './useRetailerManagement';
import { DefaultRdpManager, RdpManager } from 'core/rdp/RdpManager';
import _ from 'lodash';

interface RecommendFormProps {
  defaultForm: any;
  values: ReturnType<typeof useFormik>['values'];
  setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
}

const defaultBrandManager = new DefaultBrandManager();
const defaultRdpManager: RdpManager = new DefaultRdpManager();

/**
 *
 * @param initialState
 * @returns
 *
 */
const useRecommendForm = (
  initialState: RecommendFormProps = {
    defaultForm: {},
    values: {},
    setFieldValue: async (
      field: string,
      value: any,
      shouldValidate?: boolean,
    ) => Promise.resolve(),
  },
) => {
  const { defaultForm, values, setFieldValue } = initialState;
  const { order } = useOrderStore();
  const { actionType, showProductManager, handleLoading } = useCampaignStore();
  const { selectedProducts, selectedAutoProducts, changeSelectedAutoProducts } =
    useProductStore();
  const [diffDays, setDiffDays] = useState(1);

  // 站內品牌清單
  const [brandOptions, setBrandOptions] = useState<SelectOptions[]>([]);

  // 站內已選品牌
  const [selectBrand, setSelectBrand] = useState<string | null>(
    values.basic.webBrandNo ? values.basic.webBrandNo : '1',
  );

  const { callAPIs } = useCallAPI();
  // 廣告主管理，取得廣告主資訊
  const { filters } = useRetailerManagement({
    defaultRdpManager,
  });

  // 取得站內品牌選項
  const fetchBrandOptions = async (): Promise<void> => {
    try {
      await callAPIs(
        [
          defaultBrandManager.getRecommendBrandOptions.bind(
            defaultBrandManager,
          ),
        ],
        (brandOptions) => {
          if (
            brandOptions.some(({ value }) => value === values.basic.webBrandNo)
          ) {
            values.basic.brandName = brandOptions.filter(
              ({ value }) => value === values.basic.webBrandNo,
            )[0].label;
            setSelectBrand(values.basic.webBrandNo);
          } else {
            setSelectBrand('1');
            setFieldValue('basic.webBrandNo', null);
            setFieldValue('basic.brandName', null);
          }
          // 必須過濾掉"其他"品牌
          const preprocessBrandOptions = brandOptions.filter(
            ({ value }) => value !== '99999999999999999',
          );
          const options = [
            { value: '1', label: '全部' },
            ...preprocessBrandOptions,
          ];
          setBrandOptions(options);
        },
      );
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      handleLoading(false);
    }
  };

  // 根據已選擇的品牌取得商品
  const fetchBrandProducts = async (
    brandName: string | undefined | null,
  ): Promise<Product[] | undefined> => {
    if (!_.isEmpty(filters) && !_.isNil(filters.advertiser)) {
      const retailId = _.get(filters, 'retailerId', '');
      // const queries = handleSearchQueries(filters, categories);
      if (_.isEmpty(retailId)) {
        return undefined;
      }
      try {
        const pageable = { page: 1, sizePerPage: 100 };
        changeSelectedAutoProducts(() => []);
        let products: Product[] | undefined;
        handleLoading(true);
        await callAPIs(
          [
            defaultRdpManager.getRetailProducts.bind(
              defaultRdpManager,
              retailId,
              pageable,
              {
                isSmartRecommend: true,
                brand: brandName,
              },
            ),
          ],
          (productWithPagination: ProductWithPagination) => {
            if (productWithPagination.products.length === 0) {
              products = productWithPagination.products;
              changeSelectedAutoProducts(() => []);
              return;
            }
            changeSelectedAutoProducts(() => productWithPagination.products);
            products = productWithPagination.products;
            // fetchKeywords(productWithPagination.products);
          },
        );
        return products;
      } catch (error) {
        console.error('Failed to fetch data:', error);
        handleLoading(false);
        return undefined;
      } finally {
        handleLoading(false);
      }
    }
    return undefined;
  };

  // 品牌變更
  const handleBrandChange = (newBrand) => {
    const brandName = brandOptions.find(
      (brand) => brand.value === newBrand,
    )?.label;
    setSelectBrand(newBrand);
    if (newBrand === '1') {
      setFieldValue('basic.webBrandNo', null);
      setFieldValue('basic.brandName', null);
      fetchBrandProducts(null);
    } else {
      setFieldValue('basic.webBrandNo', newBrand);
      setFieldValue('basic.brandName', brandName);
      fetchBrandProducts(brandName);
    }
  };

  // 變更活動區間
  const handleDateRangeChange = (startDate, endDate) => {
    setFieldValue('basic.startDate', startDate);
    setFieldValue('basic.endDate', endDate);
  };
  const handleChangeStartDate = (startDate) => {
    setFieldValue('basic.startDate', startDate);
  };
  const handleChangeEndDate = (endDate) => {
    setFieldValue('basic.endDate', endDate);
  };
  // 變更預算類型
  const handleBudgetTypeChange = (newBudgetType) => {
    setFieldValue('basic.budgetType', newBudgetType);
    handleDResetAllBudget();
  };
  // 變更預算
  const handleBudgetChange = (newBudget) => {
    const { type, newPrice } = newBudget;
    if (type === BudgetType.DAILY) {
      setFieldValue('basic.dailyTargetBudget', newPrice);
      // 編輯模式下日預算模式有異動日預算才執行 handleAutoSetBudget
      if (
        actionType === ActionType.EDIT &&
        Number(newPrice) === defaultForm.basic.dailyTargetBudget
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAutoSetBudget(newPrice);
      }
    } else if (type === BudgetType.TOTAL) {
      setFieldValue('basic.budget', newPrice);
      handleAutoSetDailyBudget(newPrice);
    }
  };
  // 自動設定每日預算
  const handleAutoSetDailyBudget = (newBudget) => {
    if (values.basic.budgetType === BudgetType.TOTAL) {
      setFieldValue('basic.dailyTargetBudget', Number(newBudget).toFixed(2));
    }
  };
  // 自動設定總預算
  const handleAutoSetBudget = (newDailyBudget) => {
    const { basic } = values;
    const start = moment(basic.startDate);
    const end = moment(basic.endDate);
    const today = moment();

    // 根據狀態選擇計算天數的基準
    const baseDate =
      actionType === (ActionType.CREATE || ActionType.COPY)
        ? start
        : start.isAfter(today)
          ? start
          : today;

    // +1 是因為同日也算 1 天
    const days = end.diff(baseDate, 'days') + 1;

    if (basic.budgetType === BudgetType.DAILY) {
      const daily = Number(newDailyBudget) || 0;
      const total = basic.expectedSpent + daily * days;
      // 四捨五入到小數第二位，並用字串表示
      const formatted = (Math.round(total * 100) / 100).toFixed(2);
      setFieldValue('basic.budget', formatted);
    }
  };
  // 重置所有預算
  const handleDResetAllBudget = () => {
    setFieldValue('basic.budget', '0');
    setFieldValue('basic.dailyTargetBudget', '0');
  };
  // 切換出價選擇
  const handleBidTypeChange = (newBidMode) => {
    setFieldValue('basic.bidMode', newBidMode);
    if (newBidMode === BidMode.MANUAL) {
      setFieldValue('basic.roasStrategy', null);
    } else {
      setFieldValue('basic.roasStrategy', RoasStrategy.HIGH_ROAS);
    }
  };
  // 調整出價上限
  const handleChangeBidPrice = (newMaxBid) => {
    setFieldValue('basic.bidPrice', newMaxBid);
  };

  // 取得智能選品下拉選單
  useEffect(() => {
    fetchBrandOptions();
  }, []);

  useEffect(() => {
    // 智能選品在新增模式初始化取得商品
    if (
      actionType === ActionType.CREATE &&
      !_.isEmpty(filters) &&
      values.basic.selectPattern === SelectPattern.AUTO &&
      selectedAutoProducts?.length === 0
    ) {
      fetchBrandProducts(null);
    }
  }, [actionType, filters]);

  useEffect(() => {
    // 複製活動要根據品牌重新取得商品
    if (
      actionType === ActionType.COPY &&
      brandOptions.length > 0 &&
      !_.isEmpty(filters)
    ) {
      const brandName = brandOptions.find(
        (brand) => brand.value === defaultForm.basic.webBrandNo,
      )?.label;
      fetchBrandProducts(brandName);
    }
  }, [actionType, defaultForm.basic.webBrandNo, brandOptions, filters]);

  useEffect(() => {
    if (!showProductManager) {
      setFieldValue('products', selectedProducts);
    }
  }, [showProductManager]);

  useEffect(() => {
    setFieldValue('autoProducts', selectedAutoProducts);
  }, [selectedAutoProducts, setFieldValue]);

  useEffect(() => {
    const startDate = moment(values?.basic.startDate);
    const endDate = moment(values?.basic.endDate);
    const defaultStartDate = moment(defaultForm?.basic.startDate);
    const defaultEndDate = moment(defaultForm?.basic.endDate);
    // 需要 +1 是因為當 startDate & endDate 都是同一天時 moment.js 會判定 diff 為 0,
    // 但這邊業務需求會需要是1天
    const diffDays = endDate.diff(startDate, 'days') + 1;
    setDiffDays(diffDays);
    // 編輯模式下日預算模式有異動開始與結束日期才執行 handleAutoSetBudget
    if (values.basic.budgetType === BudgetType.DAILY) {
      if (
        actionType === ActionType.EDIT &&
        startDate.isSame(defaultStartDate) &&
        endDate.isSame(defaultEndDate) &&
        defaultForm.basic.dailyTargetBudget?.toString() ===
          values.basic.dailyTargetBudget?.toString()
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAutoSetBudget(values.basic.dailyTargetBudget);
      }
    }
  }, [
    values.basic.budgetType,
    values.basic.startDate,
    values.basic.endDate,
    values.basic.dailyTargetBudget,
  ]);

  return {
    order,
    diffDays,
    brandOptions,
    selectBrand,
    handleBrandChange,
    handleDateRangeChange,
    handleChangeStartDate,
    handleChangeEndDate,
    handleBudgetTypeChange,
    handleBudgetChange,
    handleBidTypeChange,
    handleChangeBidPrice,
    handleAutoSetBudget,
  };
};

export default useRecommendForm;
